import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeAppLanguage } from '@/app/language';

/**
 * Language row of the « Serveur en service » sheet — the handheld's only
 * settings surface, so this is where the device's language is picked.
 *
 * Not `components/chrome/LanguageSwitcher`: that one is a dropdown built for
 * the guest header, with 36px options and a hover translate. 04-ux-mobile-spec
 * §0 asks for >= 44px tap targets and no motion on this surface, and a waiter
 * changing the language must see the five choices at once rather than open a
 * second layer inside an already-open sheet. Same store (`changeAppLanguage`
 * persists to `LANG_STORAGE_KEY`), so the choice survives a reload and is the
 * same one the guest chrome reads.
 */
export default function WaiterLanguagePicker() {
  const { t, i18n } = useTranslation();
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <div className="flex flex-col gap-2">
      <p id="waiter-language-label" className="font-sans text-base text-ink-mute">
        {t('language.select')}
      </p>
      <div
        data-testid="waiter-language"
        role="group"
        aria-labelledby="waiter-language-label"
        className="grid grid-cols-3 gap-2"
      >
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isCurrent = lang.code === current.code;
          return (
            <button
              key={lang.code}
              type="button"
              lang={lang.code}
              dir={lang.dir}
              data-testid={`waiter-language-${lang.code}`}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => changeAppLanguage(lang.code)}
              className={`min-h-[48px] px-2 rounded-btn border-2 border-ink font-sans text-base font-bold truncate ${
                isCurrent ? 'bg-ink text-paper' : 'bg-paper text-ink'
              }`}
            >
              {lang.nativeLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}
